import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Check, LoaderCircle, Save, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { type UseFormSetError, useForm } from "react-hook-form";
import { z } from "zod";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { FieldError } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ApiError } from "@/lib/api";
// El catálogo de roles por obra (espejo de obras/dto.go) vive en lib/obras.ts: lo comparten
// esta feature y la ficha del usuario (#238), y una feature no importa de otra.
import { MIN_CANDIDATOS_QUERY, ROL_MIEMBRO_OPCIONES, useCandidatos } from "@/lib/obras";

// DEBOUNCE_MS espacia las llamadas de búsqueda mientras se teclea.
const DEBOUNCE_MS = 250;

const miembroSchema = z.object({
  id_usuario: z.coerce.number().int().positive("Busca y selecciona un usuario"),
  rol: z.enum(["ADMIN_OBRA", "CUALIFICADO", "AUTORIZADO", "CONSULTA"]),
});

// El esquema coacciona id_usuario (z.coerce.number): el tipo de entrada difiere del de
// salida ya validado. Con @hookform/resolvers v5 hay que tipar useForm con ambos.
type MiembroFormInput = z.input<typeof miembroSchema>;
export type MiembroFormValues = z.output<typeof miembroSchema>;

function aplicarError(
  err: unknown,
  setError: UseFormSetError<MiembroFormValues>,
  setFormError: (msg: string) => void,
): void {
  if (err instanceof ApiError) {
    if (err.fields?.id_usuario) {
      setError("id_usuario", { message: err.fields.id_usuario });
      return;
    }
    if (err.fields?.rol) {
      setError("rol", { message: err.fields.rol });
      return;
    }
    setFormError(err.message || "No se pudo asignar el miembro.");
    return;
  }
  setFormError("No se pudo asignar el miembro. Comprueba tu conexión.");
}

// useDebounced retrasa la propagación de un valor mientras se sigue tecleando.
function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/**
 * MiembroForm asigna un usuario a la obra con un rol (react-hook-form + zod). El
 * usuario se elige con la búsqueda de candidatos del backend (#161: GET
 * /obras/{id}/miembros/candidatos?q=, gate RequireObraAdmin), que devuelve usuarios
 * activos aún no miembros — accesible tanto al ADMIN global como al ADMIN_OBRA, sin
 * volcar el directorio (mínimo 2 caracteres, máx. 20 resultados). Mapea los errores
 * por campo del backend.
 */
export function MiembroForm({
  obraId,
  pending,
  onSubmit,
  onCancel,
}: {
  obraId: number;
  pending: boolean;
  onSubmit: (values: MiembroFormValues) => Promise<void>;
  onCancel: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [seleccionado, setSeleccionado] = useState<{ id: number; nombre: string } | null>(null);
  const termino = useDebounced(busqueda, DEBOUNCE_MS);
  const candidatos = useCandidatos(obraId, termino);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm<MiembroFormInput, unknown, MiembroFormValues>({
    resolver: zodResolver(miembroSchema),
    defaultValues: { id_usuario: 0, rol: "CUALIFICADO" },
  });

  function elegir(candidato: { id_usuario: number; nombre: string }) {
    setSeleccionado({ id: candidato.id_usuario, nombre: candidato.nombre });
    setValue("id_usuario", candidato.id_usuario, { shouldValidate: true });
  }

  const submit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      aplicarError(err, setError, setFormError);
    }
  });

  const buscando = termino.trim().length >= MIN_CANDIDATOS_QUERY;

  return (
    <form className="flex flex-col gap-5" onSubmit={submit} noValidate>
      {formError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>{formError}</span>
        </Alert>
      ) : null}

      {/* --- Búsqueda de candidatos --- */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="busqueda-usuario">Buscar usuario</Label>
        <div className="relative">
          <Search
            className="-translate-y-1/2 absolute top-1/2 left-3 h-5 w-5 text-slate-400"
            aria-hidden="true"
          />
          <Input
            id="busqueda-usuario"
            type="search"
            autoComplete="off"
            className="pl-10"
            placeholder="Nombre, apellidos o usuario…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        {!buscando ? (
          <p className="text-slate-500 text-sm">Escribe al menos 2 caracteres para buscar.</p>
        ) : null}

        {buscando && candidatos.isPending ? (
          <div className="flex items-center gap-2 py-2 text-slate-500 text-sm">
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            Buscando…
          </div>
        ) : null}

        {buscando && candidatos.isError ? (
          <Alert variant="destructive">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <span>No se pudieron buscar candidatos. Inténtalo de nuevo.</span>
          </Alert>
        ) : null}

        {buscando && candidatos.data && candidatos.data.length === 0 ? (
          <p className="text-slate-500 text-sm">
            Sin resultados. Solo aparecen usuarios activos que aún no son miembros.
          </p>
        ) : null}

        {buscando && candidatos.data && candidatos.data.length > 0 ? (
          <ul className="flex flex-col overflow-hidden rounded-xl border border-slate-200">
            {candidatos.data.map((c) => {
              const activo = seleccionado?.id === c.id_usuario;
              return (
                <li key={c.id_usuario}>
                  <button
                    type="button"
                    aria-pressed={activo}
                    onClick={() => elegir(c)}
                    className={`flex w-full items-center justify-between px-4 py-2.5 text-left text-base transition-colors ${
                      activo
                        ? "bg-primary-50 font-medium text-primary-800"
                        : "text-slate-800 hover:bg-slate-50"
                    }`}
                  >
                    <span>{c.nombre}</span>
                    {activo ? <Check className="h-5 w-5" aria-hidden="true" /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}

        {seleccionado ? (
          <p className="text-slate-600 text-sm">
            Seleccionado: <strong className="text-slate-900">{seleccionado.nombre}</strong>
          </p>
        ) : null}
        <input type="hidden" {...register("id_usuario")} />
        <FieldError>{errors.id_usuario?.message}</FieldError>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="rol">Rol en la obra</Label>
        <Select id="rol" aria-invalid={errors.rol ? true : undefined} {...register("rol")}>
          {ROL_MIEMBRO_OPCIONES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </Select>
        <FieldError>{errors.rol?.message}</FieldError>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancelar
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            <LoaderCircle className="h-5 w-5 animate-spin" aria-hidden="true" />
          ) : (
            <Save className="h-5 w-5" aria-hidden="true" />
          )}
          Asignar miembro
        </Button>
      </div>
    </form>
  );
}
